import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, X, FileText, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * iter195 — Admin Applications review page + detail view.
 *
 * Previously the admin panel only exposed `/admin/vendors` and
 * `/admin/businesses` (approved records). The `applications` collection
 * had 48 documents but no UI — pending signups were invisible.
 *
 * Routes registered in App.js:
 *   /admin/applications             → <AdminApplicationsList />
 *   /admin/applications/:aid        → <AdminApplicationDetail />
 *
 * The pending-count badge in the sidebar is powered by the same
 * `/api/admin/applications?status=pending` call, cached ~10s. Kept in
 * this file (see `useAdminPendingCount`) so any admin surface can reuse.
 */

const STATUS_COLORS = {
  pending: { bg: "rgba(232,184,75,0.10)", fg: "#E8B84B", border: "rgba(232,184,75,0.35)" },
  approved: { bg: "rgba(37,211,102,0.10)", fg: "#25d366", border: "rgba(37,211,102,0.35)" },
  rejected: { bg: "rgba(201,74,74,0.10)", fg: "#E88484", border: "rgba(201,74,74,0.35)" },
};

/* ─── LIST ─────────────────────────────────────────────────────────── */
export default function AdminApplicationsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("");    // "", "vendor", "business"
  const [statusFilter, setStatusFilter] = useState("");
  // iter202 — Auto-approve master toggle + approval-type filter.
  const [approvalTypeFilter, setApprovalTypeFilter] = useState("");
  const [autoApprove, setAutoApprove] = useState(true);
  const [autoBusy, setAutoBusy] = useState(false);

  const loadAutoApprove = async () => {
    try {
      const { data } = await api.get("/applications/auto-approve-state");
      setAutoApprove(!!data?.enabled);
    } catch { /* default TRUE */ }
  };

  const toggleAutoApprove = async () => {
    const next = !autoApprove;
    setAutoBusy(true);
    try {
      await api.put("/admin/applications/auto-approve", { enabled: next });
      setAutoApprove(next);
      toast.success(next
        ? "Auto-approve ON — applications go live immediately"
        : "Auto-approve OFF — manual approval required");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setAutoBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kindFilter) params.set("kind", kindFilter);
      if (statusFilter) params.set("status", statusFilter);
      if (approvalTypeFilter) params.set("approval_type", approvalTypeFilter);
      const { data } = await api.get(`/admin/applications?${params.toString()}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load applications");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); loadAutoApprove(); }, [kindFilter, statusFilter, approvalTypeFilter]); // eslint-disable-line

  const counts = useMemo(() => {
    const pending = rows.filter(r => r.status === "pending").length;
    const approved = rows.filter(r => r.status === "approved").length;
    const rejected = rows.filter(r => r.status === "rejected").length;
    return { pending, approved, rejected, total: rows.length };
  }, [rows]);

  return (
    <div data-testid="admin-applications-page" className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#F5F1E8" }}>
            Applications
          </h1>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490" }} className="mt-1">
            Review pending vendor and business signups. Approve to onboard, reject to decline.
          </p>
        </div>
        <button
          data-testid="admin-apps-refresh"
          onClick={load}
          className="px-4 py-2 rounded-lg transition-colors hover:brightness-110"
          style={{
            background: "transparent",
            border: "1px solid #E8B84B",
            color: "#E8B84B",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14, fontWeight: 600, minHeight: 40,
          }}
        >
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {["total", "pending", "approved", "rejected"].map((k) => (
          <div
            key={k}
            data-testid={`admin-apps-stat-${k}`}
            className="rounded-lg p-3"
            style={{ background: "#141414", border: "1px solid #2a2a2a" }}
          >
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9A9490", textTransform: "uppercase", letterSpacing: "1px" }}>{k}</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#F5F1E8" }}>{counts[k]}</div>
          </div>
        ))}
      </div>

      {/* iter202 — Auto-approve master toggle */}
      <section
        data-testid="admin-auto-approve-card"
        className="mb-4 rounded-xl p-4 flex items-center justify-between gap-4"
        style={{ background: "#141414", border: "1px solid #2a2a2a" }}
      >
        <div className="min-w-0">
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#F5F1E8" }}>
            Auto-Approve New Applications
          </div>
          <div className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490" }}>
            {autoApprove
              ? "ON — applications go live immediately on submission."
              : "OFF — new applications wait for a manual admin approval."}
          </div>
        </div>
        <button
          data-testid="admin-auto-approve-toggle"
          onClick={toggleAutoApprove}
          disabled={autoBusy}
          className="px-4 rounded-full shrink-0 transition-colors"
          style={{
            minWidth: 100, minHeight: 44, fontSize: 13, fontWeight: 700,
            borderRadius: 9999,
            border: `1px solid ${autoApprove ? "#E8B84B" : "rgba(232,184,75,0.30)"}`,
            color: autoApprove ? "#0B0B0B" : "#9A9490",
            background: autoApprove ? "#E8B84B" : "transparent",
          }}
        >
          {autoBusy ? "…" : (autoApprove ? "ON" : "OFF")}
        </button>
      </section>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <select
          data-testid="admin-apps-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ background: "#141414", border: "1px solid #2a2a2a", color: "#F5F1E8", padding: "8px 12px", borderRadius: 8, fontSize: 16, minHeight: 44 }}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          data-testid="admin-apps-filter-kind"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          style={{ background: "#141414", border: "1px solid #2a2a2a", color: "#F5F1E8", padding: "8px 12px", borderRadius: 8, fontSize: 16, minHeight: 44 }}
        >
          <option value="">All types</option>
          <option value="vendor">Vendor</option>
          <option value="business">Business</option>
        </select>
        <select
          data-testid="admin-apps-filter-approval-type"
          value={approvalTypeFilter}
          onChange={(e) => setApprovalTypeFilter(e.target.value)}
          style={{ background: "#141414", border: "1px solid #2a2a2a", color: "#F5F1E8", padding: "8px 12px", borderRadius: 8, fontSize: 16, minHeight: 44 }}
        >
          <option value="">All approvals</option>
          <option value="auto">Auto-approved</option>
          <option value="manual">Manually approved</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#E8B84B" }} /></div>
      ) : rows.length === 0 ? (
        <div style={{ background: "#141414", border: "1px solid #2a2a2a", borderRadius: 12 }} className="py-12 text-center">
          <FileText className="w-8 h-8 mx-auto mb-2" style={{ color: "#9A9490" }} strokeWidth={1.5} />
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#9A9490" }}>No applications match your filter.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to={`/admin/applications/${r.id}`}
                data-testid={`admin-apps-row-${r.id}`}
                className="block rounded-lg p-4 transition-colors hover:bg-white/[0.02]"
                style={{ background: "#141414", border: "1px solid #2a2a2a", minHeight: 48 }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#F5F1E8" }}>
                      {rowName(r)}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <KindPill kind={r.kind} />
                      <StatusPill status={r.status} />
                      {r.status === "approved" && r.approval_type && (
                        <span
                          data-testid={`admin-apps-approval-${r.approval_type}`}
                          style={{
                            fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700,
                            padding: "2px 8px", borderRadius: 9999,
                            background: r.approval_type === "auto" ? "rgba(232,184,75,0.10)" : "rgba(37,211,102,0.10)",
                            color: r.approval_type === "auto" ? "#E8B84B" : "#25d366",
                            border: `1px solid ${r.approval_type === "auto" ? "rgba(232,184,75,0.30)" : "rgba(37,211,102,0.30)"}`,
                          }}
                        >
                          {r.approval_type === "auto" ? "AUTO" : "MANUAL"}
                        </span>
                      )}
                      {r.suspicious && (
                        <span
                          data-testid="admin-apps-suspicious"
                          title="More than 3 applications from this IP in 24h"
                          style={{
                            fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700,
                            padding: "2px 8px", borderRadius: 9999,
                            background: "rgba(239,68,68,0.10)", color: "#ef4444",
                            border: "1px solid rgba(239,68,68,0.30)",
                          }}
                        >
                          SUSPICIOUS
                        </span>
                      )}
                      <OcrBadge status={r.id_verification_status} testid={`admin-apps-ocr-${r.id}`} />
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490" }}>
                        {formatDate(r.submitted_at)}
                      </span>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 shrink-0" style={{ color: "#9A9490" }} strokeWidth={1.5} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function rowName(r) {
  const d = r.data || {};
  return r.kind === "business" ? (d.name || "Unnamed business") : (d.full_name || "Unnamed vendor");
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

function KindPill({ kind }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
      textTransform: "uppercase", letterSpacing: "1px",
      background: "rgba(232,184,75,0.10)", border: "1px solid rgba(232,184,75,0.30)", color: "#E8B84B",
    }}>{kind}</span>
  );
}

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
      textTransform: "uppercase", letterSpacing: "1px",
      background: c.bg, border: `1px solid ${c.border}`, color: c.fg,
    }}>{status}</span>
  );
}

/**
 * iter205 — Compact OCR readability badge for the admin applications list
 * and detail views.
 *
 *   passed  → green   "OCR PASSED"
 *   failed  → red     "OCR FAILED"
 *   skipped → yellow  "OCR SKIPPED"   (Fal.ai timeout / infra issue)
 *   pending → gold    "OCR PENDING"   (background check still running)
 *
 * Returns null when the field is missing (older, pre-iter205 records).
 */
function OcrBadge({ status, testid }) {
  if (!status) return null;
  const map = {
    passed:  { label: "OCR PASSED",  fg: "#25d366", bg: "rgba(37,211,102,0.10)",  bd: "rgba(37,211,102,0.30)" },
    failed:  { label: "OCR FAILED",  fg: "#ef4444", bg: "rgba(239,68,68,0.10)",   bd: "rgba(239,68,68,0.30)"  },
    skipped: { label: "OCR SKIPPED", fg: "#E8B84B", bg: "rgba(232,184,75,0.10)",  bd: "rgba(232,184,75,0.30)" },
    pending: { label: "OCR PENDING", fg: "#C9A24A", bg: "rgba(201,162,74,0.10)",  bd: "rgba(201,162,74,0.30)" },
  };
  const c = map[status];
  if (!c) return null;
  return (
    <span
      data-testid={testid || `admin-ocr-${status}`}
      style={{
        display: "inline-block", padding: "2px 8px", borderRadius: 9999,
        fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700,
        letterSpacing: "1px",
        background: c.bg, color: c.fg, border: `1px solid ${c.bd}`,
      }}
    >
      {c.label}
    </span>
  );
}

/* ─── DETAIL ───────────────────────────────────────────────────────── */
export function AdminApplicationDetail() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      // iter195b — single-doc fetch (was: fetch full list + filter).
      const { data } = await api.get(`/admin/applications/${aid}`);
      setRow(data || null);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error("Application not found");
      } else {
        toast.error(e.response?.data?.detail || "Failed to load");
      }
    }
  };
  useEffect(() => { load(); }, [aid]); // eslint-disable-line

  const approve = async () => {
    if (!confirm("Approve this application?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/applications/${aid}/approve`);
      toast.success("Approved");
      nav("/admin/applications");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    const reason = prompt("Rejection reason (optional):");
    if (reason === null) return;
    setBusy(true);
    try {
      await api.post(`/admin/applications/${aid}/reject`, { reason });
      toast.success("Rejected");
      nav("/admin/applications");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally { setBusy(false); }
  };

  if (!row) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#E8B84B" }} /></div>;

  const d = row.data || {};
  const idUrl = d.government_id_url;
  const avatarUrl = d.avatar_url;
  const canReview = row.status === "pending";

  return (
    <div data-testid="admin-application-detail" className="max-w-3xl mx-auto px-4 py-6">
      <button
        onClick={() => nav("/admin/applications")}
        className="inline-flex items-center gap-2 mb-4"
        style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, minHeight: 40 }}
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        Back to Applications
      </button>

      <div className="flex items-center gap-2 flex-wrap mb-2">
        <KindPill kind={row.kind} />
        <StatusPill status={row.status} />
        <OcrBadge status={row.id_verification_status} testid="admin-application-ocr" />
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490" }}>
          Submitted {formatDate(row.submitted_at)}
        </span>
      </div>

      <h1
        data-testid="admin-application-name"
        style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, color: "#F5F1E8", lineHeight: 1.15 }}
      >
        {rowName(row)}
      </h1>

      <div className="mt-6 space-y-2">
        {row.kind === "business" && (
          <>
            <Row label="Category" value={d.category} />
            <Row label="Address" value={d.address} />
            <Row label="Website" value={d.website} />
            <Row label="Instagram" value={d.instagram} />
          </>
        )}
        {row.kind === "vendor" && (
          <>
            <Row label="Service" value={d.service_type} />
            <Row label="Languages" value={(d.languages || []).join(", ")} />
            <Row label="Experience" value={d.years_experience != null ? `${d.years_experience} yrs` : ""} />
          </>
        )}
        <Row label="WhatsApp" value={d.whatsapp} testid="admin-application-whatsapp" />
        <Row label="Description" value={d.description} />
        <Row label="User" value={row.user_id} />
      </div>

      {(idUrl || avatarUrl) && (
        <div className="mt-6">
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#F5F1E8" }} className="mb-3">
            Uploaded documents
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {idUrl && (
              <a
                href={idUrl}
                target="_blank" rel="noreferrer"
                data-testid="admin-application-id-image"
                className="block rounded-lg overflow-hidden"
                style={{ background: "#141414", border: "1px solid #2a2a2a" }}
              >
                {/\.pdf$/i.test(idUrl) ? (
                  <div className="p-6 grid place-items-center gap-2">
                    <FileText className="w-8 h-8" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8" }}>Open PDF</span>
                  </div>
                ) : (
                  <img src={idUrl} alt="Government ID" className="w-full h-auto" style={{ display: "block" }} />
                )}
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490" }} className="p-2 text-center">
                  Government ID (admin only)
                </div>
              </a>
            )}
            {avatarUrl && (
              <a
                href={avatarUrl}
                target="_blank" rel="noreferrer"
                data-testid="admin-application-avatar-image"
                className="block rounded-lg overflow-hidden"
                style={{ background: "#141414", border: "1px solid #2a2a2a" }}
              >
                <img src={avatarUrl} alt="Face crop" className="w-full h-auto" style={{ display: "block" }} />
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490" }} className="p-2 text-center">
                  Face crop (becomes avatar)
                </div>
              </a>
            )}
          </div>
        </div>
      )}

      {canReview && (
        <div className="mt-8 flex gap-3">
          <button
            data-testid="admin-application-approve"
            onClick={approve}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg transition-opacity"
            style={{
              minHeight: 52,
              background: busy ? "#1f1f1f" : "linear-gradient(135deg, #25d366, #1eb054)",
              color: busy ? "#9A9490" : "#0a1a10",
              fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700,
              border: "none", cursor: busy ? "wait" : "pointer",
            }}
          >
            <Check className="w-5 h-5" strokeWidth={2} /> Approve
          </button>
          <button
            data-testid="admin-application-reject"
            onClick={reject}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg transition-opacity"
            style={{
              minHeight: 52,
              background: busy ? "#1f1f1f" : "linear-gradient(135deg, #C94A4A, #9C3535)",
              color: busy ? "#9A9490" : "#F5F1E8",
              fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700,
              border: "none", cursor: busy ? "wait" : "pointer",
            }}
          >
            <X className="w-5 h-5" strokeWidth={2} /> Reject
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-3 gap-3 py-2" style={{ borderBottom: "1px solid #1f1f1f" }} data-testid={testid}>
      <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490", textTransform: "uppercase", letterSpacing: "1px" }}>{label}</div>
      <div className="col-span-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8", wordBreak: "break-word" }}>{value}</div>
    </div>
  );
}

/* ─── shared pending-count hook (used by AdminLayout nav badge) ───── */
export function useAdminPendingCount() {
  const [n, setN] = useState(0);
  useEffect(() => {
    let dead = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/applications?status=pending&limit=500");
        if (!dead) setN(Array.isArray(data) ? data.length : 0);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { dead = true; clearInterval(t); };
  }, []);
  return n;
}
